// src/pages/AdminPanel.jsx
import React, { useEffect, useRef, useState, useMemo } from 'react';
import api from '../api';

/**
 * Admin panel (updated)
 * - loads users, votes summary, products (same as before)
 * - deadline management: GET /settings, PUT /settings { votingDeadline }
 * - dispatches settingsChanged after save/clear so navbar updates instantly
 * - stops event bubbling on buttons so calendar doesn’t pop open
 * - blurs input after save to close picker
 * - NEW: search + pagination (top 10) for Users, Votes Summary, Products
 */

const SETTINGS_PATH = '/settings';
const PAGE_SIZE = 10;

// small helper for pagination
function usePaginatedList(list, search, predicate, pageState) {
  const { page, setPage } = pageState;
  const filtered = useMemo(() => {
    const q = (search || '').trim().toLowerCase();
    if (!q) return list || [];
    return (list || []).filter(item => predicate(item, q));
  }, [list, search, predicate]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = (currentPage - 1) * PAGE_SIZE;
  const end = start + PAGE_SIZE;
  const pageItems = filtered.slice(start, end);

  // if search or list changes, keep page valid
  useEffect(() => {
    const newTotalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    if (currentPage > newTotalPages) setPage(newTotalPages);
  }, [filtered.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return { pageItems, total, totalPages, currentPage };
}

export default function AdminPanel({ deadline, setDeadline }) {
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState([]);
  const [products, setProducts] = useState([]);
  const [newProd, setNewProd] = useState({ name: '', description: '', imageUrl: '' });

  // deadline states
  const [deadlineIso, setDeadlineIso] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [statusMessage, setStatusMessage] = useState(null);
  const [savingDeadline, setSavingDeadline] = useState(false);
  const inputRef = useRef(null);

  // search + pagination states (merged people table)
  const [peopleSearch, setPeopleSearch] = useState('');
  const [peoplePage, setPeoplePage] = useState(1);

  const [summarySearch, setSummarySearch] = useState('');
  const [summaryPage, setSummaryPage] = useState(1);

  const [productSearch, setProductSearch] = useState('');
  const [productPage, setProductPage] = useState(1);

  // ===== bulk selection state (only applies to rows that have userId) =====
  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());

  // ===== Allowed Employees merged with signups =====
  const [employeesMerged, setEmployeesMerged] = useState([]);
  const [showEmpModal, setShowEmpModal] = useState(false);
  const [empForm, setEmpForm] = useState({ email: '', employeeId: '' });

  const loadAll = async () => {
    try {
      const [uRes, sRes, pRes, eRes] = await Promise.all([
        api.get('/admin/users').catch(e => { console.warn('users load failed', e); return { data: [] }; }),
        api.get('/admin/votes-summary').catch(e => { console.warn('summary load failed', e); return { data: [] }; }),
        api.get('/products/admin/all').catch(e => { console.warn('products load failed', e); return { data: [] }; }),
        api.get('/admin/people-merged').catch(e => { console.warn('people load failed', e); return { data: [] }; }),
      ]);

      const usersList = Array.isArray(uRes?.data) ? uRes.data : [];
      setUsers(usersList);
      setSummary(Array.isArray(sRes?.data) ? sRes.data : []);
      setProducts(Array.isArray(pRes?.data) ? pRes.data : []);

      // backend now returns already-merged rows (allowed + users)
      setEmployeesMerged(Array.isArray(eRes?.data) ? eRes.data : []);
    } catch (err) {
      console.error('Error loading admin data', err);
      setUsers([]); setSummary([]); setProducts([]); setEmployeesMerged([]);
    }
  };

  // Load deadline from server (GET /settings → { votingDeadline })
  const loadDeadline = async () => {
    setStatusMessage(null);
    try {
      const res = await api.get(SETTINGS_PATH);
      const iso = res?.data?.votingDeadline ?? null;

      if (iso) {
        setDeadlineIso(iso);
        // convert to local datetime-local format (YYYY-MM-DDThh:mm)
        const dt = new Date(iso);
        const pad = n => String(n).padStart(2, '0');
        const localStr = `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
        setInputValue(localStr);
      } else {
        setDeadlineIso(null);
        setInputValue('');
      }
    } catch (err) {
      console.warn('Deadline load failed', err);
      setDeadlineIso(null);
      setInputValue('');
    }
  };

  useEffect(() => {
    loadAll();
    loadDeadline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset page when search changes
  useEffect(() => { setPeoplePage(1); }, [peopleSearch]);
  useEffect(() => { setSummaryPage(1); }, [summarySearch]);
  useEffect(() => { setProductPage(1); }, [productSearch]);

  const addProduct = async (e) => {
    e.preventDefault();
    try {
      await api.post('/products', newProd);
      setNewProd({ name: '', description: '', imageUrl: '' });
      loadAll();
    } catch (err) {
      console.error(err);
      alert('Add product failed');
    }
  };

  const del = async (id) => {
    if (!id) { alert('Invalid product id'); return; }
    if (!window.confirm('Delete product?')) return;
    try {
      await api.delete(`/products/${id}`);
      loadAll();
    } catch (err) {
      console.error(err);
      alert('Delete failed');
    }
  };

  // Save deadline: PUT /settings { votingDeadline: ISOString }
  const saveDeadline = async (ev) => {
    ev?.preventDefault?.();
    if (!inputValue) { setStatusMessage('Please pick a date/time'); return; }
    setSavingDeadline(true);
    setStatusMessage(null);
    try {
      const local = new Date(inputValue);
      const iso = local.toISOString();

      const res = await api.put(SETTINGS_PATH, { votingDeadline: iso });
      const saved = res?.data?.votingDeadline ?? iso;
      setDeadlineIso(saved);
      if (typeof setDeadline === 'function') setDeadline(saved);
      setStatusMessage('Deadline saved');
      try { window.dispatchEvent(new Event('settingsChanged')); } catch {}
    } catch (err) {
      console.error('Save deadline failed', err);
      const msg = err?.response?.data?.message || err?.message || 'Save deadline failed';
      setStatusMessage(msg);
    } finally {
      setSavingDeadline(false);
      try { if (inputRef.current) inputRef.current.blur(); } catch {}
    }
  };

  // Clear (PUT /settings { votingDeadline: null })
  const clearDeadline = async (ev) => {
    ev?.preventDefault?.();
    setStatusMessage(null);
    try {
      await api.put(SETTINGS_PATH, { votingDeadline: null });
      setDeadlineIso(null);
      setInputValue('');
      if (typeof setDeadline === 'function') setDeadline(null);
      setStatusMessage('Deadline cleared');
      try { window.dispatchEvent(new Event('settingsChanged')); } catch {}
    } catch (err) {
      console.error('Clear deadline failed', err);
      const msg = err?.response?.data?.message || err?.message || 'Failed to clear';
      setStatusMessage(msg);
    }
  };

  // helper to provide min attr for datetime-local (disables past dates/times)
  const getMinDateTimeLocal = () => {
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
  };

  // UI: clicking the wrapper focuses/clicks input so the full field opens the native picker (mobile/desktop)
  const onWrapperClick = () => {
    if (inputRef.current) {
      try {
        inputRef.current.focus();
        if (typeof inputRef.current.showPicker === 'function') {
          inputRef.current.showPicker();
        } else {
          inputRef.current.click();
        }
      } catch (err) {
        console.debug('Picker open attempt failed', err);
      }
    }
  };

  // ===== FILTER + PAGINATION LOGIC =====

  // small helper to get a stable user id (from merged row)
  const getRowUserId = (row) => row?.user?._id;

  // Build the dataset for the merged People table
  const peoplePager = usePaginatedList(
    Array.isArray(employeesMerged) ? employeesMerged : [],
    peopleSearch,
    (r, q) => {
      const email = (r?.email || '').toLowerCase();
      const eid = (r?.employeeId || '').toLowerCase();
      const name = (r?.user?.fullName || '').toLowerCase();
      const voted = (r?.votedProductName || '').toLowerCase();
      const signed = (r?.signedUp ? 'yes' : 'no'); // <<< include Signed up in search
      return (
        email.includes(q) ||
        eid.includes(q) ||
        name.includes(q) ||
        voted.includes(q) ||
        signed.includes(q)
      );
    },
    { page: peoplePage, setPage: setPeoplePage }
  );

  // ===== bulk action handlers (only rows with a userId can be selected) =====
  const toggleRow = (row, checked) => {
    const id = getRowUserId(row);
    if (!id) return; // no user to act on
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toggleAllOnPage = (checked) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      peoplePager.pageItems.forEach(r => {
        const id = getRowUserId(r);
        if (!id) return;
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  };

  const allOnPageSelectable = peoplePager.pageItems.filter(r => !!getRowUserId(r));
  const allOnPageSelected = allOnPageSelectable.length > 0 &&
    allOnPageSelectable.every(r => selectedUserIds.has(getRowUserId(r)));

  const selectedCount = selectedUserIds.size;

  const bulkClearVotes = async () => {
    if (selectedCount === 0) return;
    if (!window.confirm(`Clear vote for ${selectedCount} selected user(s)?`)) return;
    try {
      await api.post('/admin/users/bulk-clear-votes', { userIds: Array.from(selectedUserIds) });
      setSelectedUserIds(new Set());
      loadAll();
    } catch (err) {
      console.error(err);
      alert('Bulk clear votes failed');
    }
  };

  const bulkDeleteUsers = async () => {
    if (selectedCount === 0) return;
    if (!window.confirm(`Delete ${selectedCount} selected user(s)? This will also remove matching allow-list entries.`)) return;
    try {
      await api.post('/admin/users/bulk-delete', { userIds: Array.from(selectedUserIds) });
      setSelectedUserIds(new Set());
      loadAll();
    } catch (err) {
      console.error(err);
      alert('Bulk delete failed');
    }
  };

  // NEW: employee handlers (keep Add; removed per-row actions)
  const addEmployee = async (e) => {
    e?.preventDefault?.();
    if (!empForm.email || !empForm.employeeId) return;
    try {
      await api.post('/admin/employees', {
        email: empForm.email.trim().toLowerCase(),
        employeeId: empForm.employeeId.trim()
      });
      setEmpForm({ email: '', employeeId: '' });
      setShowEmpModal(false);
      loadAll();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || 'Add employee failed');
    }
  };

  // Reusable pagination controls
  const Pager = ({ current, totalPages, setPage }) => (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10 }}>
      <button
        onClick={() => setPage(Math.max(1, current - 1))}
        disabled={current <= 1}
        style={{ padding: '6px 10px' }}
      >
        Prev
      </button>
      <span style={{ fontSize: 13, opacity: 0.85 }}>
        Page {current} of {totalPages}
      </span>
      <button
        onClick={() => setPage(Math.min(totalPages, current + 1))}
        disabled={current >= totalPages}
        style={{ padding: '6px 10px' }}
      >
        Next
      </button>
    </div>
  );

  return (
    <div className="page">
      <h1>Admin Panel</h1>

      {/* ===== MERGED: Allowed Employees + Users (Signups) ===== */}
      <section className="admin-section">
        <h2>People (Allowed + Signups)</h2>

        <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:10 }}>
          <input
            placeholder="Search by email, employee id, name, signed up or voted product…"
            value={peopleSearch}
            onChange={(e) => setPeopleSearch(e.target.value)}
            style={{
              width: '100%',
              maxWidth: 600,
              background: '#081018',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.06)',
              padding: '10px 12px',
              borderRadius: 6
            }}
          />
          <button onClick={() => setShowEmpModal(true)}>Add Employee</button>
        </div>

        {/* Bulk actions only act on selected rows that have a user */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'end', margin: '8px 0 12px', justifyContent: 'end', float: 'right' }}>
          <button
            onClick={bulkClearVotes}
            disabled={selectedCount === 0}
            style={{ padding: '8px 12px', background: '#2d6a4f', margin: '0' }}
            title="Clear vote for selected users"
          >
            Clear
          </button>
          <button
            onClick={bulkDeleteUsers}
            disabled={selectedCount === 0}
            style={{ padding: '8px 12px', background: '#8b0000' }}
            title="Delete selected users"
          >
            Delete
          </button>
          <span style={{ fontSize: 13, opacity: 0.8 }}>
            {selectedCount > 0 ? `${selectedCount} selected` : 'No selection'}
          </span>
        </div>

        <table>
          <thead>
            <tr>
              <th style={{ width: 42, textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={allOnPageSelected}
                  onChange={(e) => toggleAllOnPage(e.target.checked)}
                  aria-label="Select all on this page"
                />
              </th>
              <th>Name</th>
              <th>Email</th>
              <th>Employee ID</th>
              <th>Signed up</th>
              <th>Voted For</th>
            </tr>
          </thead>
          <tbody>
            {peoplePager.pageItems.length === 0 && (
              <tr><td colSpan="6">No records</td></tr>
            )}
            {peoplePager.pageItems.map(r => {
              const userId = getRowUserId(r);
              const checked = userId ? selectedUserIds.has(userId) : false;
              return (
                <tr key={r._id || `${r.email}-${r.employeeId}`}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      disabled={!userId}
                      checked={checked}
                      onChange={(e) => toggleRow(r, e.target.checked)}
                      aria-label={`Select ${r.user?.fullName || r.email}`}
                    />
                  </td>
                  <td>{r.user?.fullName || '—'}</td>
                  <td>{r.email}</td>
                  <td>{r.employeeId}</td>
                  <td>{r.signedUp ? 'Yes' : 'No'}</td>
                  <td>{r.votedProductName || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <Pager
          current={peoplePager.currentPage}
          totalPages={peoplePager.totalPages}
          setPage={setPeoplePage}
        />
      </section>

      {/* ===== VOTING DEADLINE ===== */}
      <section className="admin-section">
        <h2>Voting Deadline</h2>
        <p>Set the last date/time when voting is allowed. After this date votes will be locked (no changes).</p>

        <div
          style={{
            background: 'var(--card-bg)',
            padding: 18,
            borderRadius: 10,
            maxWidth: 820
          }}
        >
          <div
            onClick={onWrapperClick}
            style={{ display: 'flex', gap: 8, alignItems: 'center', cursor: 'pointer' }}
            title="Click anywhere here to open the date/time picker"
          >
            {/* full-field clickable wrapper */}
            <input
              ref={inputRef}
              type="datetime-local"
              value={inputValue}
              onChange={e => setInputValue(e.target.value)}
              min={getMinDateTimeLocal()}
              style={{
                flex: 1,
                background: '#081018',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.06)',
                padding: '12px 14px',
                borderRadius: 6
              }}
            />
            <button
              onClick={(e) => { e.stopPropagation(); saveDeadline(e); }}
              disabled={savingDeadline}
              style={{ padding: '10px 14px' }}
            >
              {savingDeadline ? 'Saving…' : 'Save deadline'}
            </button>

            <button 
              onClick={(e) => { e.stopPropagation(); clearDeadline(e); }}
              style={{ padding: '10px 14px', background: '#666', margin: 0 }}
            >
              Clear deadline
            </button>
          </div>

          <div style={{ marginTop: 8, color: '#ffd9b3' }}>
            {deadlineIso ? (
              <span>Deadline set: <strong>{new Date(deadlineIso).toLocaleString()}</strong></span>
            ) : (
              <span>No deadline set</span>
            )}
          </div>

          {statusMessage && <div style={{ marginTop: 8, color: '#ffb3b3' }}>{statusMessage}</div>}
        </div>
      </section>

      {/* ===== VOTES SUMMARY ===== */}
      <section className="admin-section">
        <h2>Votes Summary</h2>

        {/* search */}
        <div style={{ maxWidth: 820, marginBottom: 10 }}>
          <input
            placeholder="Search by product name…"
            value={summarySearch}
            onChange={(e) => setSummarySearch(e.target.value)}
            style={{
              width: '100%',
              background: '#081018',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.06)',
              padding: '10px 12px',
              borderRadius: 6
            }}
          />
        </div>

        <ul>
          {summaryPage && summarySearch === undefined && null}
          {usePaginatedList(Array.isArray(summary) ? summary : [], summarySearch, (s, q) => {
            const productName = (s?._id?.name ?? s?._id ?? 'Unknown product').toString().toLowerCase();
            return productName.includes(q);
          }, { page: summaryPage, setPage: setSummaryPage }).pageItems.length === 0 && <li>No votes yet.</li>}
          {usePaginatedList(Array.isArray(summary) ? summary : [], summarySearch, (s, q) => {
            const productName = (s?._id?.name ?? s?._id ?? 'Unknown product').toString().toLowerCase();
            return productName.includes(q);
          }, { page: summaryPage, setPage: setSummaryPage }).pageItems.map((s, idx) => {
            const idKey = (s?._id?._id) || (s?._id) || `summary-${idx}`;
            const productName = s?._id?.name ?? s?._id ?? 'Unknown product';
            const votesCount = s?.votes ?? 0;
            return (
              <li key={String(idKey)} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                <strong>{productName}</strong>
                <span>— {votesCount} votes</span>
              </li>
            );
          })}
        </ul>

        <Pager
          current={usePaginatedList(Array.isArray(summary) ? summary : [], summarySearch, (s, q) => {
            const productName = (s?._id?.name ?? s?._id ?? 'Unknown product').toString().toLowerCase();
            return productName.includes(q);
          }, { page: summaryPage, setPage: setSummaryPage }).currentPage}
          totalPages={usePaginatedList(Array.isArray(summary) ? summary : [], summarySearch, (s, q) => {
            const productName = (s?._id?.name ?? s?._id ?? 'Unknown product').toString().toLowerCase();
            return productName.includes(q);
          }, { page: summaryPage, setPage: setSummaryPage }).totalPages}
          setPage={setSummaryPage}
        />
      </section>

      {/* ===== PRODUCTS ===== */}
      <section className="admin-section">
        <h2>Products</h2>

        {/* search */}
        <div style={{ maxWidth: 820, marginBottom: 10 }}>
          <input
            placeholder="Search products by name or description…"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            style={{
              width: '100%',
              background: '#081018',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.06)',
              padding: '10px 12px',
              borderRadius: 6
            }}
          />
        </div>

        <form onSubmit={addProduct} className="form-inline" style={{ marginBottom:60 }}>
          <input placeholder="Name" value={newProd.name} onChange={e => setNewProd({ ...newProd, name: e.target.value })} required />
          <input placeholder="Image URL" value={newProd.imageUrl} onChange={e => setNewProd({ ...newProd, imageUrl: e.target.value })} />
          <input placeholder="Description" value={newProd.description} onChange={e => setNewProd({ ...newProd, description: e.target.value })} />
          <button type="submit">Add</button>
        </form>

        <ul>
          {usePaginatedList(Array.isArray(products) ? products : [], productSearch, (p, q) => {
            const name = (p?.name || '').toLowerCase();
            const desc = (p?.description || '').toLowerCase();
            return name.includes(q) || desc.includes(q);
          }, { page: productPage, setPage: setProductPage }).pageItems.length === 0 && <li>No products yet</li>}
          {usePaginatedList(Array.isArray(products) ? products : [], productSearch, (p, q) => {
            const name = (p?.name || '').toLowerCase();
            const desc = (p?.description || '').toLowerCase();
            return name.includes(q) || desc.includes(q);
          }, { page: productPage, setPage: setProductPage }).pageItems.map(p => (
            <li key={p._id || p.name || Math.random()} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <strong>{p?.name || 'Unnamed'}</strong> — {p?.active ? 'Active' : 'Inactive'}
              </div>
              <button onClick={() => del(p?._id)}>Delete</button>
            </li>
          ))}
        </ul>

        <Pager
          current={usePaginatedList(Array.isArray(products) ? products : [], productSearch, (p, q) => {
            const name = (p?.name || '').toLowerCase();
            const desc = (p?.description || '').toLowerCase();
            return name.includes(q) || desc.includes(q);
          }, { page: productPage, setPage: setProductPage }).currentPage}
          totalPages={usePaginatedList(Array.isArray(products) ? products : [], productSearch, (p, q) => {
            const name = (p?.name || '').toLowerCase();
            const desc = (p?.description || '').toLowerCase();
            return name.includes(q) || desc.includes(q);
          }, { page: productPage, setPage: setProductPage }).totalPages}
          setPage={setProductPage}
        />
      </section>

      {/* Modal for adding employee */}
      {showEmpModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', display:'grid', placeItems:'center', zIndex:10000 }}>
          <form onSubmit={addEmployee} style={{ background:'var(--color-surface)', border:'1px solid var(--color-border)', borderRadius:12, padding:20, width:'min(90vw,420px)', boxShadow:'0 10px 30px rgba(0,0,0,.5)' }}>
            <h3 style={{ marginTop:0, color:'var(--color-accent)' }}>Add Allowed Employee</h3>
            <input
              placeholder="Official Email"
              type="email"
              value={empForm.email}
              onChange={(e)=>setEmpForm(f=>({...f, email:e.target.value}))}
              required
            />
            <input
              placeholder="Employee ID"
              value={empForm.employeeId}
              onChange={(e)=>setEmpForm(f=>({...f, employeeId:e.target.value}))}
              required
            />
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:10 }}>
              <button type="button" onClick={()=>setShowEmpModal(false)} style={{ background:'#666' }}>Cancel</button>
              <button type="submit">Add</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
